import * as React from 'react';
import { useEffect, useRef } from 'react';
import { classes } from '@bloomberg/media-ui-core';
import { Search as SearchIcon } from '@bloomberg/media-ui-icons';

import styles from './SearchDesktopJapan.module.css';

export type SearchDesktopJapanProps = {
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  buttonRef: React.RefObject<HTMLButtonElement>;
};

export const SearchDesktopJapan = ({
  isOpen,
  onToggle,
  onClose,
  buttonRef,
}: SearchDesktopJapanProps) => {
  const searchContainerRef = useRef<HTMLDivElement>(null);

  // Load Google Custom Search Engine script
  useEffect(() => {
    const script = document.createElement('script');

    script.src = 'https://cse.google.com/cse.js?cx=60d37b10a9757480c';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  // Position and size results dynamically based on search input location
  // so that it
  // - doesn't interfere with the ad
  // - doesn't scroll off the page
  // - isn't positioned oddly when the user first scrolls a little then clicks search
  // - adjusts height based on available viewport space (200px min, 780px max)
  useEffect(() => {
    if (!isOpen || !searchContainerRef.current) {
      return undefined;
    }

    const positionResults = () => {
      const resultsWrapper = document.querySelector<HTMLElement>(
        '.gsc-results-wrapper-nooverlay, .gsc-results-wrapper-overlay, .gsc-results-wrapper-visible'
      );

      if (resultsWrapper && searchContainerRef.current) {
        const rect = searchContainerRef.current.getBoundingClientRect();
        // 10px below the search input
        const desiredTop = rect.bottom + 10;
        // Never less than 10px from top of viewport
        const minTop = 10;
        const topPosition = Math.max(minTop, desiredTop);

        // Calculate dynamic height based on available space
        const bottomPadding = 20;
        const viewportHeight = window.innerHeight;
        const availableHeight = viewportHeight - topPosition - bottomPadding;
        // Constrain between 200px and 780px
        const height = Math.max(200, Math.min(780, availableHeight));

        resultsWrapper.style.top = `${topPosition}px`;
        resultsWrapper.style.left = '50%';
        resultsWrapper.style.transform = 'translateX(-50%)';
        resultsWrapper.style.height = `${height}px`;
      }
    };

    // Use requestAnimationFrame to ensure DOM has updated
    requestAnimationFrame(() => {
      positionResults();
      window.addEventListener('scroll', positionResults);
      window.addEventListener('resize', positionResults);
    });

    return () => {
      window.removeEventListener('scroll', positionResults);
      window.removeEventListener('resize', positionResults);
    };
  }, [isOpen]);

  // Focus input when search opens
  useEffect(() => {
    if (isOpen) {
      const input = document.querySelector<HTMLInputElement>(
        '.gsc-input input.gsc-input, input.gsc-input'
      );

      if (input) {
        input.focus();
      }
    }
  }, [isOpen]);

  return (
    <div className={styles.container}>
      {!isOpen && (
        <button
          ref={buttonRef}
          className={styles.searchButton}
          onClick={onToggle}
          data-testid="nav-japan-search-button"
        >
          <SearchIcon />
        </button>
      )}
      <div
        className={classes([
          styles.searchInline,
          { [styles.searchInlineOpen]: isOpen },
        ])}
      >
        <div className={styles.searchContainer} ref={searchContainerRef}>
          <div className="gcse-search"></div>
        </div>
        {isOpen && (
          <button
            className={styles.closeButton}
            onClick={onClose}
            data-testid="nav-japan-search-close"
          >
            キャンセル
          </button>
        )}
      </div>
    </div>
  );
};
